//deps
import React, { memo, ReactElement, useMemo } from "react";
import cx from "classnames";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

//components
import { BottomSheet, BottomSheetChildrenProps } from "@core-ui/components/bottom-sheet";
import { Image } from "@core-ui/components/image";

//hooks
import { useFocusTrap } from "@core-ui/hooks/UseFocusTrap";

//helper
import { getCloudinaryUrl } from "@core-ui/helpers/Image";

//constants
import { ALERT_ICON } from "@core-ui/components/generic-alert/constants";

//styles
import styles from "./GenericAlert.scss";

interface GenericAlertAction {
    text: string;
    onClick: VoidFunction;
    subText?: string;
    className?: string;
}

export interface GenericAlertProps {
    icon?: string;
    iconCloudinaryUrl?: string;
    title?: string;
    subTitle?: string;
    primaryAction: GenericAlertAction;
    secondaryAction?: GenericAlertAction;
    hideOnOverlayClick?: boolean;
    onClose: VoidFunction;
    onClickCloseButton?: VoidFunction;
    hideIcon?: boolean;
    hideCloseIcon?: boolean;
    containerDataTestId?: string;
    primaryCtaDataTestId?: string;
    secondaryCtaDataTestId?: string;
    closeButtonDataTestId?: string;
    titleDataTestId?: string;
    subTitleDataTestId?: string;
    titleClassName?: string;
    subTitleClassName?: string;
    containerClassName?: string;
    closeButtonClassName?: string;
}

function GenericAlert(props: GenericAlertProps): ReactElement {
    const {
        primaryAction,
        hideIcon,
        hideOnOverlayClick,
        icon = ALERT_ICON,
        iconCloudinaryUrl,
        onClose,
        secondaryAction,
        subTitle,
        title,
        hideCloseIcon,
        onClickCloseButton,
        containerDataTestId,
        primaryCtaDataTestId,
        secondaryCtaDataTestId,
        closeButtonDataTestId,
        subTitleDataTestId,
        titleDataTestId,
        titleClassName,
        subTitleClassName,
        containerClassName,
        closeButtonClassName,
    } = props;

    const iconUrl = useMemo(() => {
        return iconCloudinaryUrl ?? getCloudinaryUrl(icon);
    }, [icon, iconCloudinaryUrl]);

    const handlePrimaryActionClick = (closeHalfCard: () => void) => {
        return (): void => {
            primaryAction.onClick();
            closeHalfCard();
        };
    };
    const handleSecondaryActionClick = (closeHalfCard: () => void) => {
        return (): void => {
            secondaryAction?.onClick();
            closeHalfCard();
        };
    };

    const secondaryCtaClass = cx(secondaryAction?.className, styles.ctaSecondary);
    const titleClass = cx(styles.genericAlertMainContentTitle, titleClassName);
    const subTitleClass = cx(styles.genericAlertMainContentSubtitle, subTitleClassName);
    const containerClass = cx(styles.genericAlertContainer, containerClassName);

    const genericAlertContent = useMemo(() => {
        if (!title && !subTitle) {
            return null;
        }
        return (
            <div className={styles.genericAlertMainContent}>
                {title && (
                    <div className={titleClass} data-testid={titleDataTestId}>
                        {title}
                    </div>
                )}
                {subTitle && (
                    <div className={subTitleClass} data-testid={subTitleDataTestId}>
                        {subTitle}
                    </div>
                )}
            </div>
        );
    }, [subTitle, subTitleClass, subTitleDataTestId, title, titleClass, titleDataTestId]);

    useFocusTrap();

    return (
        <BottomSheet
            hideOnOverlayClick={hideOnOverlayClick}
            onClose={onClose}
            containerClass={containerClass}
            hideCloseIcon={hideCloseIcon}
            onClickCloseButton={onClickCloseButton}
            containerDataTestId={containerDataTestId}
            closeButtonDataTestId={closeButtonDataTestId}
            closeButtonClassName={closeButtonClassName}
        >
            {({ close }: BottomSheetChildrenProps): ReactElement => {
                return (
                    <>
                        <div className={styles.genericAlertMain}>
                            {!hideIcon && <Image imageUrl={iconUrl} className={styles.genericAlertMainIcon} />}
                            {genericAlertContent}
                        </div>
                        <div className={styles.genericAlertCTAWrapper}>
                            <SuperButton
                                onClick={handlePrimaryActionClick(close)}
                                data-testid={primaryCtaDataTestId}
                                buttonType="primary"
                                buttonSize="large"
                                variant="fullwidth"
                                className={primaryAction.className}
                                subLabel={primaryAction.subText}
                            >
                                {primaryAction.text}
                            </SuperButton>
                            {secondaryAction && (
                                <SuperButton
                                    onClick={handleSecondaryActionClick(close)}
                                    className={secondaryCtaClass}
                                    data-testid={secondaryCtaDataTestId}
                                    buttonType="secondary"
                                    buttonSize="large"
                                    variant="fullwidth"
                                    subLabel={secondaryAction.subText}
                                >
                                    {secondaryAction.text}
                                </SuperButton>
                            )}
                        </div>
                    </>
                );
            }}
        </BottomSheet>
    );
}

export default memo(GenericAlert);
